import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { type Truck, type SiteZone, type LoadingQueueEntry } from './types';
import { ThemePalette } from 'mui.theme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const QUEUE_STATUS_COLOR: Record<string, string> = {
  LOADING: '#4ade80',
  HAULING: '#4ade80',
  RETURNING: '#a78bfa',
  QUEUED: '#fbbf24',
  DOWN: '#f87171',
};

const QUEUE_STATUS_BORDER: Record<string, string> = {
  LOADING: '#4ade80',
  HAULING: '#4ade80',
  RETURNING: '#a78bfa',
  QUEUED: '#fbbf24',
  DOWN: '#f87171',
};

interface Props {
  trucks: Truck[];
  sites: SiteZone[];
  onSelectTruck: (id: string) => void;
  selectedId: string | null;
  enableDarkTheme: boolean;
}

const AssignmentPanel: React.FC<Props> = ({
  trucks,
  sites,
  onSelectTruck,
  selectedId,
  enableDarkTheme,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [selectedLoadingId, setSelectedLoadingId] = useState<string | null>(
    null
  );

  const cardBg = enableDarkTheme
    ? palette.boxBackground
    : palette.boxBackground;
  const sectionBg = enableDarkTheme
    ? 'rgba(10,15,30,0.8)'
    : 'rgba(240,244,255,0.8)';
  const locationBg = enableDarkTheme
    ? 'rgba(26,39,68,0.6)'
    : 'rgba(230,236,255,0.6)';
  const borderCol = palette.cardBorderColor;
  const textPrimary = enableDarkTheme ? '#e2e8f0' : '#1a202c';
  const textMuted = enableDarkTheme ? '#6b7280' : '#8A92A0';
  const textSub = enableDarkTheme ? '#8899bb' : '#6b7280';
  const statValue = enableDarkTheme ? '#c9d8f0' : '#424242';
  const tableHeaderBg = enableDarkTheme
    ? 'rgba(99,102,241,0.1)'
    : 'rgba(99,102,241,0.06)';

  const loadingSites = sites.filter((s) => s.type === 'loading');
  const dumpSites = sites.filter((s) => s.type === 'dumping');
  const selectedLoading =
    loadingSites.find((s) => s.id === selectedLoadingId) ?? null;

  function getBumperForTruck(truckId: string): string {
    const t = trucks.find((x) => x.id === truckId);
    return t?.bumperNumber ?? truckId;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        background: enableDarkTheme
          ? palette.pageBackground
          : palette.pageBackground,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: borderCol,
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
        }}
      >
        {/* ── Top mini-KPIs ── */}
        <Box
          sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}
        >
          <Box
            sx={{
              background: sectionBg,
              border: `1px solid ${borderCol}`,
              borderRadius: '6px',
              padding: '10px 12px',
            }}
          >
            <Typography
              sx={{
                fontSize: '9px',
                fontWeight: 600,
                color: textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: '6px',
              }}
            >
              {t('dm_assign_loading_points')}
            </Typography>
            <Typography
              sx={{ fontSize: '18px', fontWeight: 600, color: '#4ade80' }}
            >
              {loadingSites.length}
            </Typography>
          </Box>
          <Box
            sx={{
              background: sectionBg,
              border: `1px solid ${borderCol}`,
              borderRadius: '6px',
              padding: '10px 12px',
            }}
          >
            <Typography
              sx={{
                fontSize: '9px',
                fontWeight: 600,
                color: textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: '6px',
              }}
            >
              {t('dm_assign_dumping_points')}
            </Typography>
            <Typography
              sx={{ fontSize: '18px', fontWeight: 600, color: '#a5b4fc' }}
            >
              {dumpSites.length}
            </Typography>
          </Box>
        </Box>

        {/* ── Loading Points section ── */}
        <Box
          sx={{
            background: sectionBg,
            border: `1px solid ${borderCol}`,
            borderRadius: '6px',
            padding: '12px',
          }}
        >
          {/* Section header */}
          {!selectedLoading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: '8px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#6366f1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                📦 {t('dm_assign_loading_points_title')}
              </Typography>
              <Box
                sx={{
                  ml: 'auto',
                  width: '20px',
                  height: '20px',
                  background: 'rgba(99,102,241,0.2)',
                  borderRadius: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 600,
                  color: '#a5b4fc',
                }}
              >
                {loadingSites.length}
              </Box>
            </Box>
          )}

          {/* Detail view */}
          {selectedLoading ? (
            <Box>
              <Box sx={{ mb: '8px' }}>
                <Typography
                  onClick={() => {
                    setSelectedLoadingId(null);
                  }}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#6366f1',
                    cursor: 'pointer',
                    display: 'inline',
                    '&:hover': { opacity: 0.75 },
                  }}
                >
                  {t('dm_assign_back_to_loading')}
                </Typography>
              </Box>

              {/* Detail header card */}
              <Box
                sx={{
                  background: locationBg,
                  border: `1px solid ${borderCol}`,
                  borderRadius: '6px',
                  padding: '12px',
                  mb: '8px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    mb: '4px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: textPrimary,
                    }}
                  >
                    {selectedLoading.name}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: '10px', color: textSub, mb: '10px' }}
                >
                  {selectedLoading.grade} {t('avg_cycle')}{' '}
                  {selectedLoading.avgCycle} {t('cdp_break_minutes_label')}
                </Typography>

                {/* 3 stats */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '10px',
                  }}
                >
                  {[
                    {
                      label: t('dm_assign_material_qty'),
                      value: `${selectedLoading.materialQty}t`,
                    },
                    {
                      label: t('dm_assign_shovels'),
                      value: selectedLoading.shovels ?? '—',
                    },
                    {
                      label: t('dm_assign_trucks_assigned'),
                      value: selectedLoading.trucksAssigned ?? '—',
                    },
                  ].map((stat) => (
                    <Box
                      key={stat.label}
                      sx={{
                        background: enableDarkTheme
                          ? 'rgba(10,15,30,0.8)'
                          : 'rgba(240,244,255,0.9)',
                        border: `0.5px solid ${borderCol}`,
                        borderRadius: '4px',
                        padding: '8px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#a5b4fc',
                          lineHeight: 1,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '8px',
                          color: textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          mt: '4px',
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Truck queue */}
              <Box
                sx={{
                  background: locationBg,
                  border: `0.5px solid ${borderCol}`,
                  borderRadius: '5px',
                  padding: '10px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: statValue,
                    mb: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {t('dm_assign_truck_queue')}
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
                >
                  {(selectedLoading.queue ?? []).map(
                    (entry: LoadingQueueEntry) => {
                      const borderColor =
                        QUEUE_STATUS_BORDER[entry.status] ?? '#6b7280';
                      const statusColor =
                        QUEUE_STATUS_COLOR[entry.status] ?? '#6b7280';
                      const bumper = getBumperForTruck(entry.truckId);
                      const isSelected = selectedId === entry.truckId;

                      return (
                        <Box
                          key={entry.truckId}
                          onClick={() => {
                            onSelectTruck(entry.truckId);
                          }}
                          sx={{
                            background: isSelected
                              ? 'rgba(99,102,241,0.1)'
                              : enableDarkTheme
                              ? 'rgba(10,15,30,0.8)'
                              : 'rgba(255,255,255,0.6)',
                            borderLeft: `3px solid ${borderColor}`,
                            borderRadius: '5px',
                            padding: '8px 10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '9px',
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                            '&:hover': { background: 'rgba(99,102,241,0.07)' },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '9px',
                              fontWeight: 600,
                              color: statValue,
                              minWidth: '70px',
                            }}
                          >
                            {bumper}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '9px',
                              fontWeight: 600,
                              color: statusColor,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              minWidth: '68px',
                            }}
                          >
                            {entry.status}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '9px',
                              color: textSub,
                              flex: 1,
                            }}
                          >
                            {entry.operatorFirstName}
                          </Typography>
                        </Box>
                      );
                    }
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            /* List view */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {loadingSites.map((site) => (
                <Box
                  key={site.id}
                  onClick={() => {
                    setSelectedLoadingId(site.id);
                  }}
                  sx={{
                    background: locationBg,
                    border: `0.5px solid ${borderCol}`,
                    borderRadius: '5px',
                    padding: '10px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { background: 'rgba(99,102,241,0.07)' },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: statValue,
                      mb: '8px',
                    }}
                  >
                    {site.name}
                  </Typography>
                  {[
                    { label: t('dm_assign_grade'), value: site.grade ?? '—' },
                    {
                      label: t('dm_assign_material_qty'),
                      value: `${site.materialQty}t`,
                    },
                    {
                      label: t('dm_assign_shovels'),
                      value: `${site.shovels} · Trucks: ${site.trucksAssigned}`,
                    },
                    {
                      label: t('dm_assign_efficiency'),
                      value: `${site.efficiency} t/hr`,
                      green: true,
                    },
                  ].map((row: any) => (
                    <Box
                      key={row.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: '3px',
                      }}
                    >
                      <Typography sx={{ fontSize: '9px', color: textMuted }}>
                        {row.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '9px',
                          fontWeight: 500,
                          color: row.green ? '#4ade80' : textSub,
                        }}
                      >
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* ── Dumping Points section ── */}
        <Box
          sx={{
            background: sectionBg,
            border: `1px solid ${borderCol}`,
            borderRadius: '6px',
            padding: '12px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
            <Typography
              sx={{ fontSize: '11px', fontWeight: 600, color: '#6366f1' }}
            >
              🗑️ {t('dm_assign_dumping_points_title')}
            </Typography>
            <Box
              sx={{
                ml: 'auto',
                width: '20px',
                height: '20px',
                background: 'rgba(99,102,241,0.2)',
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 600,
                color: '#a5b4fc',
              }}
            >
              {dumpSites.length}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {dumpSites.map((site) => {
              const materials = (site.materials ?? []) as Array<{
                grade: string;
                targetRatio: number;
                targetTons: number;
                actualTons: number;
              }>;
              const totalActual = materials.reduce(
                (sum, m) => sum + m.actualTons,
                0
              );
              const totalTarget = materials.reduce(
                (sum, m) => sum + m.targetTons,
                0
              );
              const totalVariance = totalActual - totalTarget;

              return (
                <Box
                  key={site.id}
                  sx={{
                    background: locationBg,
                    border: `0.5px solid ${borderCol}`,
                    borderRadius: '5px',
                    padding: '10px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: statValue,
                      mb: '6px',
                    }}
                  >
                    {site.name}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: '3px',
                    }}
                  >
                    <Typography sx={{ fontSize: '9px', color: textMuted }}>
                      {t('dm_assign_target_total')}
                    </Typography>
                    <Typography
                      sx={{ fontSize: '9px', color: textSub, fontWeight: 500 }}
                    >
                      {site.targetTotal} t
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: '6px',
                    }}
                  >
                    <Typography sx={{ fontSize: '9px', color: textMuted }}>
                      {t('dm_assign_total_dumped')}
                    </Typography>
                    <Typography
                      sx={{ fontSize: '9px', color: textSub, fontWeight: 500 }}
                    >
                      {totalActual.toFixed(0)} t
                    </Typography>
                  </Box>

                  {/* Blend table */}
                  <Box
                    component="table"
                    sx={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '8px',
                    }}
                  >
                    <Box component="thead">
                      <Box component="tr">
                        {[
                          t('dm_assign_grade'),
                          t('dm_assign_ratio'),
                          t('dm_assign_target'),
                          t('dm_assign_actual'),
                        ].map((h) => (
                          <Box
                            component="th"
                            key={h}
                            sx={{
                              background: tableHeaderBg,
                              color: textMuted,
                              padding: '5px 4px',
                              textAlign: 'left',
                              fontWeight: 600,
                              borderBottom: `0.5px solid ${borderCol}`,
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                            }}
                          >
                            {h}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {materials.map((mat) => {
                        const variance = mat.actualTons - mat.targetTons;
                        const onTrack = mat.actualTons >= mat.targetTons;
                        return (
                          <Box component="tr" key={mat.grade}>
                            <Box
                              component="td"
                              sx={{
                                padding: '5px 4px',
                                borderBottom: `0.5px solid ${borderCol}33`,
                                color: '#a5b4fc',
                                fontWeight: 500,
                              }}
                            >
                              {mat.grade}
                            </Box>
                            <Box
                              component="td"
                              sx={{
                                padding: '5px 4px',
                                borderBottom: `0.5px solid ${borderCol}33`,
                                color: textSub,
                              }}
                            >
                              {mat.targetRatio}%
                            </Box>
                            <Box
                              component="td"
                              sx={{
                                padding: '5px 4px',
                                borderBottom: `0.5px solid ${borderCol}33`,
                                color: textSub,
                              }}
                            >
                              {mat.targetTons.toFixed(0)}
                            </Box>
                            <Box
                              component="td"
                              sx={{
                                padding: '5px 4px',
                                borderBottom: `0.5px solid ${borderCol}33`,
                                color: statValue,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {mat.actualTons.toFixed(0)}
                              {variance !== 0 && (
                                <Box
                                  component="span"
                                  sx={{
                                    ml: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '28px',
                                    height: '16px',
                                    borderRadius: '10px',
                                    fontSize: '7px',
                                    fontWeight: 700,
                                    color: onTrack ? '#4ade80' : '#f87171',
                                    background: onTrack
                                      ? 'rgba(74,222,128,0.15)'
                                      : 'rgba(248,113,113,0.15)',
                                    border: `1px solid ${
                                      onTrack
                                        ? 'rgba(74,222,128,0.3)'
                                        : 'rgba(248,113,113,0.3)'
                                    }`,
                                  }}
                                >
                                  {variance > 0 ? '+' : ''}
                                  {variance.toFixed(0)}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    {/* Total row */}
                    <Box component="tfoot">
                      <Box component="tr">
                        <Box
                          component="td"
                          colSpan={2}
                          sx={{
                            padding: '5px 4px',
                            borderTop: `0.5px solid ${borderCol}`,
                            fontWeight: 600,
                            color: textPrimary,
                            background: 'rgba(99,102,241,0.05)',
                            fontSize: '8px',
                          }}
                        >
                          {t('dm_assign_total')}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            padding: '5px 4px',
                            borderTop: `0.5px solid ${borderCol}`,
                            fontWeight: 600,
                            color: textPrimary,
                            background: 'rgba(99,102,241,0.05)',
                          }}
                        >
                          {totalTarget.toFixed(0)}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            padding: '5px 4px',
                            borderTop: `0.5px solid ${borderCol}`,
                            fontWeight: 600,
                            color: textPrimary,
                            background: 'rgba(99,102,241,0.05)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {totalActual.toFixed(0)}
                          {totalVariance !== 0 && (
                            <Box
                              component="span"
                              sx={{
                                ml: '4px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '16px',
                                borderRadius: '10px',
                                fontSize: '7px',
                                fontWeight: 700,
                                color:
                                  totalVariance >= 0 ? '#4ade80' : '#f87171',
                                background:
                                  totalVariance >= 0
                                    ? 'rgba(74,222,128,0.15)'
                                    : 'rgba(248,113,113,0.15)',
                                border: `1px solid ${
                                  totalVariance >= 0
                                    ? 'rgba(74,222,128,0.3)'
                                    : 'rgba(248,113,113,0.3)'
                                }`,
                              }}
                            >
                              {totalVariance > 0 ? '+' : ''}
                              {totalVariance.toFixed(0)}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AssignmentPanel;
